import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  Radio,
  Result,
  Select,
  Skeleton,
  Switch,
  Typography,
  message,
} from "antd";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useGlobal } from "../../contexts/GlobalContext";
import request from "../../utils/request";
import {
  canOpenOnboardingStep,
  installSnippet,
  mergeStepDraft,
  normalizeOnboarding,
  onboardingDraftKey,
  onboardingSteps,
  type OnboardingState,
} from "./siteOnboardingModel";
import InstallDiagnostics from "./InstallDiagnostics";
import "./SiteOnboarding.css";

const { Title, Text, Paragraph } = Typography;

interface SiteDetail {
  id: string;
  name: string;
  primaryUrl: string;
  siteType: string;
  description: string;
  legacyProjectId?: number;
}

const installMethods = [
  ["cms", "CMS 插件", "WordPress、Halo 等后台直接安装"],
  ["coding-agent", "Coding Agent", "让开发助手完成代码接入"],
  ["script", "一行脚本", "适合绝大多数主题和静态网站"],
  ["npm", "npm", "适合 React、Vue 等工程项目"],
  ["server", "服务端 API", "由服务端托管身份和会话"],
] as const;

function readLocalDraft(siteId: string, step: number): Record<string, unknown> | undefined {
  try {
    const value = localStorage.getItem(onboardingDraftKey(siteId, step));
    return value ? JSON.parse(value) : undefined;
  } catch {
    return undefined;
  }
}

const StepFields: React.FC<{
  step: number;
  siteId: string;
  installMethod?: string;
}> = ({ step, siteId, installMethod }) => {
  if (step === 1) {
    return (
      <>
        <Form.Item name="name" label="网站名称" rules={[{ required: true, message: "请输入网站名称" }, { max: 128 }]}>
          <Input autoFocus placeholder="例如：洪墨 AI 帮助中心" />
        </Form.Item>
        <Form.Item name="primaryUrl" label="网站 URL" rules={[{ required: true, message: "请输入网站 URL" }, { type: "url", message: "请输入完整的 http 或 https 地址" }]}>
          <Input placeholder="https://example.com" />
        </Form.Item>
        <div className="onboarding-form-grid">
          <Form.Item name="siteType" label="网站类型" rules={[{ required: true }]}>
            <Select options={[
              { value: "content", label: "内容 / 博客" },
              { value: "docs", label: "文档 / 帮助中心" },
              { value: "community", label: "社区 / 论坛" },
              { value: "commerce", label: "电商 / 服务" },
              { value: "other", label: "其他" },
            ]} />
          </Form.Item>
          <Form.Item name="purpose" label="主要用途" rules={[{ required: true, message: "请说明希望 AI 帮助访客完成什么" }]}>
            <Input placeholder="例如：回答产品问题并引导提交工单" />
          </Form.Item>
        </div>
        <Alert showIcon type="info" message="保存后会异步探测 CMS 与 Sitemap；探测失败不会阻止手工安装。" />
      </>
    );
  }
  if (step === 2) {
    return (
      <>
        <Form.Item name="installMethod" rules={[{ required: true, message: "请选择安装方式" }]}>
          <Radio.Group className="install-methods">
            {installMethods.map(([value, title, description]) => (
              <Radio.Button value={value} key={value}>
                <strong>{title}</strong><small>{description}</small>
              </Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>
        <div className="install-code" aria-label="安装代码">
          <span>按当前方式安装</span>
          <pre>{installSnippet(siteId, installMethod || "script")}</pre>
        </div>
      </>
    );
  }
  if (step === 3) {
    return (
      <>
        <InstallDiagnostics siteId={siteId} />
        <Form.Item name="manualContinue" valuePropName="checked" rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error("请确认已完成当前安装操作")) }]}>
          <Checkbox>我已查看诊断；若仍有问题，将继续手工安装而不丢失进度</Checkbox>
        </Form.Item>
      </>
    );
  }
  if (step === 4) {
    return (
      <div className="onboarding-switches">
        <Form.Item name="publicSync" label="同步公开站点内容" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Text type="secondary">默认开启。发现、提取、去重和索引进度会在知识页面持续展示。</Text>
        <Form.Item name="visitorCrawl" label="访客访问时补充抓取" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Text type="secondary">默认关闭，避免未经检查的内容直接进入站点知识。</Text>
      </div>
    );
  }
  if (step === 5) {
    return (
      <>
        <Form.Item name="answerEnabled" label="当前页与全站问答" valuePropName="checked"><Switch /></Form.Item>
        <Form.Item name="operationMode" label="页面操作档位">
          <Radio.Group className="ability-levels">
            <Radio value="readonly">只读回答</Radio>
            <Radio value="advice">建议操作（推荐）</Radio>
            <Radio value="confirm">确认后执行</Radio>
          </Radio.Group>
        </Form.Item>
        <Alert showIcon type="success" message="安全默认值：AI 可以高亮、滚动并说明步骤，但不会点击、填写或产生写入。" />
      </>
    );
  }
  if (step === 6) {
    return (
      <>
        <Paragraph>我们会在测试实验室按网站类型生成问题和任务。首次建议检查：</Paragraph>
        <ul className="generated-tests">
          <li>访客能否从当前页获得带来源的回答</li>
          <li>站内知识不足时是否明确说明不知道</li>
          <li>建议操作是否只高亮和说明，不产生写入</li>
        </ul>
        <Form.Item name="testConfirmed" valuePropName="checked" rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error("请确认进入测试实验室检查")) }]}>
          <Checkbox>进入测试实验室前，先保存这组安全测试</Checkbox>
        </Form.Item>
      </>
    );
  }
  return (
    <>
      <div className="publish-checklist">
        {[
          ["安装", "已完成接入步骤"],
          ["知识", "公开内容同步策略已保存"],
          ["风险", "页面操作保持建议档位"],
          ["标识", "默认展示 AI 与隐私入口"],
          ["外观", "使用安全默认主题"],
        ].map(([label, detail]) => <p key={label}><i className="ri-checkbox-circle-fill" /><strong>{label}</strong><span>{detail}</span></p>)}
      </div>
      <Form.Item name="audience" label="首批可见范围">
        <Radio.Group><Radio value="owner">仅站长本人（推荐）</Radio></Radio.Group>
      </Form.Item>
      <Alert showIcon type="info" message="本次只创建站长灰度，不会直接向全部访客开放。" />
    </>
  );
};

const SiteOnboarding: React.FC = () => {
  const { siteId = "new" } = useParams<{ siteId: string }>();
  const isNew = siteId === "new";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { API_BASE_URL } = useGlobal();
  const [form] = Form.useForm();
  const installMethod = Form.useWatch("installMethod", form);
  const [site, setSite] = useState<SiteDetail>();
  const [state, setState] = useState<OnboardingState>(() => normalizeOnboarding(undefined));
  const [activeStep, setActiveStep] = useState(1);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [finished, setFinished] = useState(false);
  const [localSavedAt, setLocalSavedAt] = useState<string>();

  const load = useCallback(async (signal?: AbortSignal) => {
    if (isNew) return;
    setLoading(true);
    try {
      const [siteResponse, onboardingResponse] = await Promise.all([
        request.get(`${API_BASE_URL}/api/v3/sites/${encodeURIComponent(siteId)}`, { signal }),
        request.get(`${API_BASE_URL}/api/v3/sites/${encodeURIComponent(siteId)}/onboarding`, { signal }),
      ]);
      if (siteResponse.data?.code !== 200) throw new Error(siteResponse.data?.msg || "网站不存在");
      if (onboardingResponse.data?.code !== 200) throw new Error(onboardingResponse.data?.msg || "接入进度读取失败");
      const nextState = normalizeOnboarding(onboardingResponse.data.data);
      setSite(siteResponse.data.data);
      setState(nextState);
      const requestedStep = Number(searchParams.get("step"));
      setActiveStep(requestedStep >= 1 && requestedStep <= 7 && canOpenOnboardingStep(nextState, requestedStep) ? requestedStep : nextState.currentStep);
    } catch (error: any) {
      if (error?.code !== "ERR_CANCELED") message.error(error.response?.data?.msg || error.message || "接入进度读取失败");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [API_BASE_URL, isNew, searchParams, siteId]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    const draft = mergeStepDraft(state, activeStep, readLocalDraft(siteId, activeStep));
    if (activeStep === 1 && site) {
      form.setFieldsValue({ ...draft, name: site.name, primaryUrl: site.primaryUrl, siteType: site.siteType, purpose: draft.purpose || site.description });
    } else {
      form.setFieldsValue(draft);
    }
  }, [activeStep, form, site, siteId, state]);

  const activeDefinition = onboardingSteps[activeStep - 1];
  const savedLabel = useMemo(() => {
    if (localSavedAt) return `本地草稿已保存 ${localSavedAt}`;
    if (state.updatedAt) return `云端已保存 ${new Date(state.updatedAt).toLocaleString()}`;
    return "完成一步后自动保存到云端";
  }, [localSavedAt, state.updatedAt]);

  const saveLocal = (_: unknown, values: Record<string, unknown>) => {
    try {
      localStorage.setItem(onboardingDraftKey(siteId, activeStep), JSON.stringify(values));
      setLocalSavedAt(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    } catch {
      // Private browsing can reject storage; the form itself remains intact.
    }
  };

  const saveStep = async () => {
    // 校验失败时 antd 会 reject 一个 {errorFields} 普通对象;必须在此吞掉,
    // 否则未捕获的非 Error 拒绝会冒泡成开发遮罩「[object Object]」/生产 unhandledrejection。
    // 字段级错误由 antd 自动展示,这里静默返回即可。
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    let effectiveSiteId = siteId;
    try {
      if (isNew) {
        const createResponse = await request.post(`${API_BASE_URL}/api/v3/sites`, {
          name: values.name,
          primaryUrl: values.primaryUrl,
          siteType: values.siteType,
          description: values.purpose,
        });
        if (createResponse.data?.code !== 200) throw new Error(createResponse.data?.msg || "网站创建失败");
        effectiveSiteId = createResponse.data.data.id;
        const onboardingResponse = await request.get(`${API_BASE_URL}/api/v3/sites/${encodeURIComponent(effectiveSiteId)}/onboarding`);
        if (onboardingResponse.data?.code !== 200) throw new Error(onboardingResponse.data?.msg || "接入状态创建失败");
        setState(normalizeOnboarding(onboardingResponse.data.data));
      } else if (activeStep === 1) {
        const updateResponse = await request.put(`${API_BASE_URL}/api/v3/sites/${encodeURIComponent(effectiveSiteId)}`, {
          name: values.name,
          primaryUrl: values.primaryUrl,
          description: values.purpose,
        });
        if (updateResponse.data?.code !== 200) throw new Error(updateResponse.data?.msg || "网站信息保存失败");
        setSite(updateResponse.data.data);
      }

      const currentRevision = isNew ? 1 : state.revision;
      const saveResponse = await request.put(`${API_BASE_URL}/api/v3/sites/${encodeURIComponent(effectiveSiteId)}/onboarding`, {
        step: activeStep,
        completed: true,
        data: values,
        expectedRevision: currentRevision,
      });
      if (saveResponse.data?.code !== 200) throw new Error(saveResponse.data?.msg || "接入进度保存失败");
      const nextState = normalizeOnboarding(saveResponse.data.data);
      localStorage.removeItem(onboardingDraftKey(siteId, activeStep));
      if (isNew) {
        localStorage.removeItem(onboardingDraftKey("new", activeStep));
        navigate(`/sites/${encodeURIComponent(effectiveSiteId)}/setup`, { replace: true });
      }
      setState(nextState);
      setLocalSavedAt(undefined);
      if (activeStep === 7 && nextState.status === "completed") {
        setFinished(true);
        message.success("站长灰度已准备完成");
      } else {
        setActiveStep(nextState.currentStep);
        message.success(`第 ${activeStep} 步已保存`);
      }
    } catch (error: any) {
      saveLocal(null, values);
      const requestId = error.response?.headers?.["x-request-id"] || error.response?.data?.request_id;
      message.error(`${error.response?.data?.msg || error.message || "保存失败，已保留本地草稿"}${requestId ? `（请求 ${requestId}）` : ""}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <main className="site-onboarding"><Skeleton active paragraph={{ rows: 10 }} /></main>;
  if (state.mode === "legacy_ready") {
    return <main className="site-onboarding"><Result status="success" title="旧站点已自动接入" subTitle="原项目、知识与 SDK 能力保持可用，无需重新完成向导。" extra={<Button type="primary" onClick={() => navigate("/sites")}>返回网站中心</Button>} /></main>;
  }
  if (finished) {
    return <main className="site-onboarding"><Result status="success" title="网站已完成接入" subTitle="当前仅站长本人可见。你可以先测试真实问答和操作，再逐步扩大范围。" extra={[<Button type="primary" key="test" onClick={() => navigate("/sites")}>进入网站中心</Button>, <Button key="stay" onClick={() => setFinished(false)}>查看接入设置</Button>]} /></main>;
  }

  return (
    <main className="site-onboarding" aria-labelledby="onboarding-title">
      <header className="site-onboarding__header">
        <Button type="text" icon={<i className="ri-close-line" />} onClick={() => navigate("/sites")}>退出，稍后继续</Button>
        <span>{savedLabel}</span>
      </header>
      <div className="site-onboarding__layout">
        <aside className="onboarding-steps" aria-label="接入步骤">
          <Text type="secondary">网站接入</Text>
          <Title level={3}>{site?.name || "新网站"}</Title>
          <ol>
            {onboardingSteps.map((step) => {
              const completed = state.completedSteps.includes(step.id);
              const accessible = isNew ? step.id === 1 : canOpenOnboardingStep(state, step.id);
              return (
                <li key={step.id} className={`${activeStep === step.id ? "is-active" : ""} ${completed ? "is-complete" : ""}`}>
                  <button disabled={!accessible} onClick={() => accessible && setActiveStep(step.id)}>
                    <span>{completed ? <i className="ri-check-line" /> : step.id}</span>
                    <span><strong>{step.title}</strong><small>{step.summary}</small></span>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>
        <section className="onboarding-panel">
          <Text type="secondary">第 {activeStep} 步，共 7 步</Text>
          <Title id="onboarding-title" level={2}>{activeDefinition.title}</Title>
          <Paragraph type="secondary">{activeDefinition.summary}。这里只展示完成接入所需的最少选项。</Paragraph>
          <Form form={form} layout="vertical" onValuesChange={saveLocal} className="onboarding-form">
            <StepFields step={activeStep} siteId={isNew ? "" : siteId} installMethod={installMethod} />
          </Form>
          <footer className="onboarding-actions">
            <Button disabled={activeStep === 1} onClick={() => setActiveStep(Math.max(1, activeStep - 1))}>上一步</Button>
            <Button type="primary" size="large" loading={saving} onClick={() => void saveStep()}>
              {activeStep === 7 ? "仅向我发布" : "保存并继续"}<i className="ri-arrow-right-line" />
            </Button>
          </footer>
        </section>
      </div>
    </main>
  );
};

export default SiteOnboarding;
